import React from "react";
import { FiCheckCircle, FiAlertCircle, FiInfo, FiArrowLeft, FiShield } from "react-icons/fi";
import { useNavigate } from "react-router-dom";
import { Helmet } from "react-helmet";

export default function TermsOfService() {
  const navigate = useNavigate();

  // Schema.org for Legal Documentation
  const tosSchema = {
    "@context": "https://schema.org",
    "@type": "WebPage",
    "name": "Terms and Conditions - protech Construction",
    "description": "Standard terms of service for contracting, project management, and industrial services by protech Construction.",
    "publisher": {
      "@type": "Organization",
      "name": "protech Construction"
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] font-sans text-slate-900">
      <Helmet>
        <title>Terms & Conditions | protech Construction & Services</title>
        <meta name="description" content="Official Terms of Service for protech Construction. Legal guidelines for petroleum projects, industrial contracting, and project management services." />
        <meta name="robots" content="noindex, follow" />
        <link rel="canonical" href="https://protech.construction/terms-conditions" />
        <script type="application/ld+json">{JSON.stringify(tosSchema)}</script>
      </Helmet>

      {/* Navigation */}
      <nav className="p-6 bg-white border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <button 
            onClick={() => navigate("/")}
            className="text-[#0054A6] font-bold flex items-center gap-2 hover:text-[#EE3124] transition-all group"
          >
            <FiArrowLeft className="group-hover:-translate-x-1 transition-transform" /> 
            Back to Home
          </button>
          <span className="font-black text-slate-800 uppercase tracking-widest text-xs hidden sm:block">
            Legal Department
          </span>
        </div>
      </nav>

      <main className="max-w-4xl mx-auto py-16 px-6">
        {/* Header Section */}
        <header className="mb-12">
          <div className="flex items-center gap-4 mb-4">
            <div className="w-12 h-12 bg-slate-900 rounded-2xl flex items-center justify-center text-white shadow-xl -rotate-3">
              <FiInfo size={24} />
            </div>
            <h1 className="text-4xl md:text-5xl font-black text-slate-900 tracking-tight">
              Terms of <span className="text-[#EE3124]">Service.</span>
            </h1>
          </div>
          <p className="text-slate-500 font-medium">
            Last Updated: May 2026 | protech Construction & Services
          </p>
        </header>

        {/* Content Body */}
        <div className="bg-white rounded-[2.5rem] p-8 md:p-12 border border-slate-200 shadow-2xl shadow-slate-200/50 space-y-10 text-slate-600 leading-relaxed">
          
          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-3">
              <FiCheckCircle className="text-[#0054A6]" /> 1. Scope of Engagement
            </h2>
            <p>
              By engaging with protech Construction & Services via this portal or official tenders, you agree to provide 
              accurate project specifications. Our services include <strong>Petroleum infrastructure maintenance, 
              civil engineering, and Central Government project execution</strong>. All engagements are subject 
              to site feasibility and statutory clearances.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-3">
              <FiAlertCircle className="text-[#EE3124]" /> 2. Project Site Conduct
            </h2>
            <p>
              All personnel, vendors, and supervisors accessing protech Construction project sites must adhere 
              to <strong>Petroleum safety standards (OISD)</strong>. Unauthorized photography, distribution 
              of project blueprints, or breach of safety protocols will result in immediate termination of 
              access and potential legal action.
            </p>
          </section>

          <section className="bg-slate-50 p-6 rounded-2xl border-l-4 border-[#0054A6]">
            <h2 className="text-xl font-black text-slate-800 mb-4 flex items-center gap-3">
              <FiShield className="text-[#0054A6]" /> 3. Intellectual Property
            </h2>
            <p>
              All engineering designs, dashboard analytics, and technical reports provided through our 
              services are the intellectual property of <strong>protech Construction</strong>. Users may not 
              reverse engineer our management systems or reuse technical blueprints without written consent.
            </p>
          </section>

          <section>
            <h2 className="text-xl font-black text-slate-800 mb-4">4. Limitation of Liability</h2>
            <p>
              While we pride ourselves on project excellence, protech Construction is not liable for delays 
              caused by <strong>Force Majeure</strong> (natural disasters, government policy changes, or 
              material shortages). We do not guarantee specific financial outcomes for vendors using our 
              procurement portal.
            </p>
          </section>

          {/* Legal Jurisdiction Box */}
          <div className="bg-red-50 p-6 rounded-2xl border border-red-100 flex flex-col md:flex-row gap-4 items-center">
            <div className="text-red-600 font-bold text-sm uppercase tracking-widest shrink-0">
              Legal Jurisdiction
            </div>
            <p className="text-sm font-bold text-red-800 leading-tight">
              These terms are governed by the laws of Uttar Pradesh, India. Any legal disputes shall be 
              subject to the exclusive jurisdiction of the Honorable Courts in Lucknow.
            </p>
          </div>
        </div>

        <footer className="mt-12 text-center text-slate-400 text-sm">
          © 2026 protech Construction & Services. <br />
          Official Communication: protechconstruction0004@gmail.com
        </footer>
      </main>
    </div>
  );
}